import { omit } from 'lodash';
import moment from 'moment';

import beCreateAppointment from 'actions/appointment/beCreateAppointment.action';
import beUpdateAppointment from 'actions/appointment/beUpdateAppointment.action';
import { CHARGE_NOSHOW_FAILURE, CHARGE_NOSHOW_REQUEST, CHARGE_NOSHOW_SUCCESS } from 'actions/payment/beChargeCardOnFile.action';
import { deleteAllAppointmentsV3 } from 'actions/scheduler/deleteAllAppointments.action';
import {
  aTypeCreateUpdateTimeoff,
  aTypeFetchEventsBySalon,
  aTypeFetchProceduresBySalon,
  aTypeGetProcedureLog,
  aTypeMarkAppointmentEdit,
  aTypeRebook,
  aTypeUndoProcedure,
  aTypeUpdateProcedure,
} from 'actions/shared.types';
import { feSetStylistId } from 'actions/ui.actions';
import RRPT from 'constants/recurrence/recurrenceRuleParseType.const';
import getAny from 'utils/accessors/getAny.util';
import asArray from 'utils/arrays/asArray.util';
import recurrenceRuleSet from 'utils/recurrence/recurrenceRuleSet.util';

import {
  CHANGE_APPOINTMENT_STATUS_REQUEST,
  CHANGE_APPOINTMENT_STATUS_SUCCESS,
  CREATE_APPOINTMENT_WITH_SERVICES_SUCCESS,
  CREATE_DEPOSIT_BACK_END_FAILURE,
  CREATE_DEPOSIT_BACK_END_SUCCESS,
  DELETE_APPOINTMENT_REQUEST,
  GET_PREFERENCE_SUCCESS,
  UPDATE_APPOINTMENT_WITH_SERVICES_REQUEST,
  UPDATE_APPOINTMENT_WITH_SERVICES_SUCCESS,
} from 'booking/constants';
import { CHECKOUT_CLOSE_SESSION_SUCCESS } from 'checkout/constants';
import { SMS_CREDITS_PUSHER } from 'clientCommunications/constants';
import { createIndex, putListInList } from 'lib/redux_functions';
import {
  CLEAR_NOSHOW_CHARGE_INFO,
  CLEAR_REQUEST_DEPOSIT_SUCCESS,
  DELETE_PREFERENCE_SUCCESS,
  EMPTY_APPOINTMENT_STATUS,
  FETCH_APPOINTMENT_DETAILS_FAILURE,
  FETCH_APPOINTMENT_DETAILS_REQUEST,
  FETCH_APPOINTMENT_DETAILS_SUCCESS,
  FETCH_CLIENT_LABELS_SUCCESS,
  HIDE_INACTIVE_STYLISTS,
  REQUEST_DEPOSIT_FAILURE,
  REQUEST_DEPOSIT_REQUEST,
  REQUEST_DEPOSIT_SUCCESS,
  SAVE_DAYDATE,
  SAVE_PREFERENCE_SUCCESS,
  SCHEDULER_ALLOW_CREATION,
  SCHEDULER_ALLOW_MODAL,
  SCHEDULER_FETCH_DEPOSIT_FAILURE,
  SCHEDULER_FETCH_DEPOSIT_REQUEST,
  SCHEDULER_FETCH_DEPOSIT_SUCCESS,
  SCHEDULER_UPDATED_APPOINTMENT,
  SCHEDULER_UPDATED_EVENT,
  SCHEDULER_UPDATED_PROCEDURE,
  SET_BOOKED_BY_BUTTON,
  SET_DRAG_FROM,
  SET_DROP_TIME,
  SET_VIEW,
  SHOW_ALL_STYLISTS,
  STYLISTS_SAVE_ORDER_REQUEST,
  STYLISTS_SAVE_ORDER_SUCCESS,
} from 'scheduler/constants';

const createDateIndex = (items) => {
  if (!items || !items.length) return {};

  // loop through the items and create a date index
  const result = {};
  for (let i = 0; i < items.length; i += 1) {
    const itemDate = moment(items[i].start_time).startOf('day');
    if (!(itemDate in result)) {
      result[itemDate] = [];
    }
    result[itemDate].push(i);
  }
  return result;
};

const addSingleEventIntoList = (oldList, newItem) => {
  const combinedList = [...asArray(oldList)];
  let updated = false;

  for (let i = 0; i < oldList.length; i += 1) {
    if (oldList[i].id !== newItem.id) continue;

    if (!newItem.recurrences) {
      combinedList[i] = newItem;
      updated = true;
    } else if (moment(oldList[i].start_time).get('date') === moment(newItem.start_time).get('date')) {
      // repeating entries have the same ID
      combinedList[i] = newItem;
      updated = true;
    } // if-else-if
  } // for

  if (!updated) combinedList.push(newItem);

  return combinedList;
};

export const SCHEDULE_REDUCER_DEFAULT_STATE = {
  recurringToBeChanged: 0,
  dayView: true,
  defaultToWeekForOneStylist: true,
  selectedDate: moment(),
  currentDay: moment(),
  isSalonOpen: true,
  stylistId: {},
  selected_stylist: {},
  preferenceList: [],
  isFetching: false,
  isChargingNoshow: false,
  noshowChargeSuccess: false,
  noshowChargeError: false,
  waiting_procedures: false,
  waiting_events: false,
  update_events: false,
  errorMessage: '',
  data: null,
  procedures: [],
  procedures_date_index: {},
  procedures_index: {},
  startTime: moment(new Date().setHours(8)).startOf('hour'),
  endTime: moment(new Date().setHours(20)).startOf('hour'),
  events: [],
  displayStylists: [],
  stylistSaveOrder: {
    isFetching: false,
    response: null,
    error: null,
  },
  procedureLog: {
    isFetching: false,
    response: null,
    error: null,
  },
  appointmentDetails: {},
  isClientBirthdayToday: false,
  show_only_active: false,
  allowCreation: true,
  allowCreationCount: 0,
  allowModal: true,
  allowModalCount: 0,
  deposits: {},
  bookedByButton: false,
  procedureDropTimeUndone: false,
  procedureDropTime: false,
  noshowChargedAmount: 0,
  noshow_appointment_id: 0,
  depositData: {},
  noshow_action: '',
  isPosDeposit: false,
  isRequestingDeposit: false,
  requestDepositSuccess: false,
  procedureChanged: null,
  hideDeletedStylist: true,
  procedureDragFrom: 0,
  appointmentCancelled: false,
  checkoutClosed: false,
};

const scheduleReducer = (state = SCHEDULE_REDUCER_DEFAULT_STATE, action) => {
  const innerAction = action?.data?.action;
  const innerData = action?.data?.data?.data;

  switch (action?.type) {
    case feSetStylistId.AT.name: {
      return {
        ...state,
        stylistId: action.data?.id,
        stylist: action.data,
      };
    }

    case aTypeFetchProceduresBySalon.init:
      return { ...state, waiting_procedures: true };

    case aTypeFetchProceduresBySalon.pass: {
      const newResponses = action.response.map((r) => ({ ...r, responseReceived: moment(action.responseReceived) }));
      const cutoffTime = moment(action.responseReceived).subtract(30, 'minutes');
      const receivedIds = newResponses.map((p) => p.id);
      let filteredProcedures = state.procedures.filter((p) => p.responseReceived && moment(p.responseReceived).isAfter(cutoffTime) && !receivedIds.includes(p.id));
      if (filteredProcedures.length === 0 && state.procedures.length > 0) {
        // We've filtered everything, so let's not filter this time, but do the filter next successful request
        filteredProcedures = [...state.procedures.filter((p) => !receivedIds.includes(p.id))];
      }
      const newProcedures = newResponses.concat(filteredProcedures);
      return {
        ...state,
        procedures: newProcedures,
        procedures_date_index: createDateIndex(newProcedures),
        procedures_index: createIndex(newProcedures),
        isFetching: false,
        waiting_procedures: false,
      };
    }

    case aTypeFetchProceduresBySalon.fail:
      return {
        ...state,
        isFetching: false,
        waiting_procedures: false,
      };

    case aTypeUpdateProcedure.init:
    case deleteAllAppointmentsV3.AT.init: {
      const procedures = putListInList(state.procedures, [{ ...(action?.params?.procedure ?? {}) }]);
      return {
        ...state,
        procedures,
        procedures_date_index: createDateIndex(procedures),
        procedures_index: createIndex(procedures),
        update: { isFetching: true, errorMessage: '' },
      };
    }

    case aTypeUpdateProcedure.pass:
    case deleteAllAppointmentsV3.AT.pass: {
      if (!action.config || action.config.method !== 'delete') return { ...state };

      const deletedIds = action.params.procedure.map((p) => p.id);
      const deletedProcedures = state.procedures.filter((p) => deletedIds.indexOf(p.id) >= 0).map((p) => ({ ...p, appointment: { ...p.appointment, status: 'deleted' } }));

      return { ...state, procedures: { ...state.procedures, ...deletedProcedures } };
    }

    case SCHEDULER_UPDATED_PROCEDURE: {
      const { procedures: originalProcedures } = state;
      let {
        data: {
          data: { data: newProcedure },
        },
      } = action;
      newProcedure = { ...newProcedure, responseReceived: moment() };
      let updatedProcedures;
      if (innerAction === 'CREATE') {
        updatedProcedures = putListInList(originalProcedures, [newProcedure]);
      } else if (innerAction === 'DELETE') {
        let deletedIndex;
        if (newProcedure.procedures) {
          deletedIndex = originalProcedures.findIndex((p) => p.id === newProcedure.procedures[0].id);
        } else {
          // remove a service when editing booking
          deletedIndex = originalProcedures.findIndex((p) => p.id === newProcedure.id);
        }
        if (deletedIndex >= 0) {
          originalProcedures.splice(deletedIndex, 1);
        }
        updatedProcedures = originalProcedures;
      } else if (innerAction === 'UPDATE') {
        const changedIndex = originalProcedures.findIndex((p) => p.id === newProcedure.id);
        if (changedIndex >= 0) {
          originalProcedures[changedIndex] = newProcedure;
        }
        updatedProcedures = originalProcedures;
      }
      return {
        ...state,
        procedures: updatedProcedures,
        procedures_date_index: updatedProcedures ? createDateIndex(updatedProcedures) : 0,
        procedures_index: createIndex(updatedProcedures),
      };
    }

    case SCHEDULER_UPDATED_EVENT: {
      const originalEvents = state.events.slice();
      const id1 = innerData.id;

      if (innerAction !== 'DELETE') {
        const newEvents = state.events.slice();
        const foundIndex = newEvents.findIndex((e) => e.id === id1);
        if (foundIndex >= 0) return { ...state, update_events: true };

        let filteredEvents = newEvents.slice();

        // if (newEvents.length > 0) {
        newEvents.push(innerData);
        for (let i = 0; i < newEvents.length; i += 1) {
          newEvents[i].local_id = i + 1;
        }
        // }
        filteredEvents = newEvents;
        return {
          ...state,
          events: filteredEvents,
          update_events: true,
        };
      }
      // let filteredEvents;
      if (innerAction === 'DELETE') {
        let deleteIndex = -1;
        let afterDeleteEvents = [];
        if (!innerData.update_all) {
          deleteIndex = originalEvents.findIndex((e) => e.id === id1 && moment(e.start_time).get(Date).format('DD-MM-YY') === moment(innerData.start_time).get(Date).format('DD-MM-YY'));
        } else {
          afterDeleteEvents = originalEvents.filter((e) => e.id !== id1);
        }
        if (deleteIndex >= 0) {
          originalEvents.splice(deleteIndex, 1);
          afterDeleteEvents = originalEvents;
        }

        return {
          ...state,
          events: afterDeleteEvents,
          update_events: true,
        };
      }

      const updatedEvents = putListInList(originalEvents, [innerData]);

      return { ...state, events: updatedEvents };
    }

    case SCHEDULER_UPDATED_APPOINTMENT: {
      const { procedures: originalProcedures } = state;
      const {
        data: { action: updateType },
      } = action;
      switch (updateType) {
        case 'UPDATE':
        case 'CREATE': {
          let {
            data: {
              data: {
                data: { procedures: newProcedures },
              },
            },
          } = action;
          newProcedures = newProcedures.map((p) => ({ ...p, responseReceived: moment() }));
          const updatedProcedures = putListInList(originalProcedures, newProcedures);
          return {
            ...state,
            procedures: updatedProcedures,
            procedures_date_index: createDateIndex(updatedProcedures),
            procedures_index: createIndex(updatedProcedures),
          };
        }
        case 'DELETE': {
          const appointment = innerData;
          // let appId;
          const {
            data: {
              data: {
                data: { procedures },
              },
            },
          } = action;
          let updatedProcedures;
          if (appointment && appointment.is_repeating) {
            const parentIdArr = procedures.map((p) => (p.parent_id ? p.parent_id : p.id));

            // if we are deleting not from the first repeat booking
            if (procedures.parent_id) {
              updatedProcedures = originalProcedures.filter((proc) => parentIdArr.indexOf(proc.parent_id) < 0 && parentIdArr.indexOf(proc.id) < 0);
            } else {
              // we are deleting from the first which don't have parent_id
              updatedProcedures = originalProcedures.filter((proc) => parentIdArr.indexOf(proc.parent_id) < 0 && parentIdArr.indexOf(proc.id) < 0);
            }
          } else if (appointment && appointment.id) {
            updatedProcedures = originalProcedures.filter((proc) => proc.appointment.id !== appointment.id);
          }
          return {
            ...state,
            procedures: updatedProcedures,
            procedures_date_index: createDateIndex(updatedProcedures),
            procedures_index: createIndex(updatedProcedures),
          };
        }
        default:
          return { ...state };
      }
    }

    case aTypeRebook.name: {
      // appointmentCancelled used by Scheduler to show rebook or send sms when rebook or send sms we need to clear it
      return { ...state, isReBooking: true, appointmentCancelled: false };
    }

    case EMPTY_APPOINTMENT_STATUS: {
      return { ...state, isReBooking: false, appointmentCancelled: false };
    }

    case beCreateAppointment.AT.pass: {
      const { procedures: originalProcedures, isReBooking, appointmentDetails } = state;
      const updatedProcedures = putListInList(originalProcedures, action.json.procedures);

      // appointmentDetails was used to decide whether to show the chargeNoshow modal
      // as we don't want to show it after creating new appointment, so reset it

      return {
        ...state,
        procedures: updatedProcedures,
        procedures_date_index: createDateIndex(updatedProcedures),
        procedures_index: createIndex(updatedProcedures),
        appointmentDetails: isReBooking ? appointmentDetails : {},
        isPosDeposit: true,
      };
    }

    case CREATE_APPOINTMENT_WITH_SERVICES_SUCCESS: {
      const { procedures: originalProcedures, isReBooking, appointmentDetails } = state;
      const updatedProcedures = putListInList(originalProcedures, action.json.procedures);

      // appointmentDetails was used to decide whether to show the chargeNoshow modal
      // as we don't want to show it after creating new appointment, so reset it

      return {
        ...state,
        procedures: updatedProcedures,
        procedures_date_index: createDateIndex(updatedProcedures),
        procedures_index: createIndex(updatedProcedures),
        appointmentDetails: isReBooking ? appointmentDetails : {},
      };
    }

    case CREATE_DEPOSIT_BACK_END_SUCCESS: {
      return { ...state, isPosDeposit: false };
    }

    case CREATE_DEPOSIT_BACK_END_FAILURE: {
      return { ...state, isPosDeposit: false };
    }

    case beUpdateAppointment.AT.init: {
      const { id } = action.params.data.procedures[0];
      const procedures = action.params.data.procedures.slice();
      const { start_time: startTime } = procedures[0];

      return {
        ...state,
        procedureChanged: { id, start_time: startTime },
      };
    }

    case beUpdateAppointment.AT.pass: {
      const { procedures: originalProcedures } = state;
      const {
        response: { procedures: newProcedures },
      } = action;
      const updatedProcedures = putListInList(
        originalProcedures.filter((p) => p.appointment.id !== action.response.id),
        newProcedures,
      );
      return {
        ...state,
        procedures: updatedProcedures,
        procedures_date_index: createDateIndex(updatedProcedures),
        procedures_index: createIndex(updatedProcedures),
      };
    }

    case UPDATE_APPOINTMENT_WITH_SERVICES_SUCCESS: {
      // const updatedProcedure = action.response.procedures.filter(p => p.id === state.procedureChanged.id)[0];
      const { procedures: originalProcedures } = state;
      const {
        response: { procedures: newProcedures },
      } = action;
      const updatedProcedures = putListInList(
        originalProcedures.filter((p) => p.appointment.id !== action.response.id),
        newProcedures,
      );
      return {
        ...state,
        procedures: updatedProcedures,
        procedures_date_index: createDateIndex(updatedProcedures),
        procedures_index: createIndex(updatedProcedures),
      };
    }

    case CHANGE_APPOINTMENT_STATUS_SUCCESS: {
      const { procedures: originalProcedures } = state;
      const updatedProcedures = originalProcedures.slice();
      let updatedAppointment = null;

      for (let i = 0; i < updatedProcedures.length; i += 1) {
        if (updatedProcedures[i].appointment.id === action.response[0].id) {
          updatedProcedures[i].appointment.status = action.response[0].status;
          updatedAppointment = updatedProcedures[i].appointment;
        }
      }

      return {
        ...state,
        procedures: updatedProcedures,
        appointmentCancelled: action.response[0].status === 'canceled',
        lastChangedAppointmentStatus: updatedAppointment,
        isFetching: false,
      };
    }

    case aTypeFetchEventsBySalon.init:
      return { ...state, waiting_events: true, update_events: false };

    case aTypeFetchEventsBySalon.pass: {
      // Strip the client records
      const { events } = state;
      const justEvents = action.response.map((o) => {
        if (o.client) {
          const name = o.client.user_name;
          Object.assign(o, { client_name: name });
        } else {
          Object.assign(o, { client_name: 'Client Name' });
        }
        omit(o, 'client');
        return o;
      });

      const allEvents = asArray(justEvents).reduce(addSingleEventIntoList, [...asArray(events)]);
      if (allEvents.length > 0) {
        for (let i = 0; i < allEvents.length; i += 1) {
          allEvents[i].local_id = i + 1;
        }
      }

      return {
        ...state,
        events: allEvents,
        isFetching: false,
        waiting_events: false,
        update_events: false,
      };
    }

    case aTypeFetchEventsBySalon.fail:
      return { ...state, isFetching: false, waiting_events: false, update_events: false };

    case aTypeCreateUpdateTimeoff.init:
      return { ...state, isFetching: true };

    case aTypeCreateUpdateTimeoff.pass: {
      let newEvents = state.events.slice();
      let filteredEvents = newEvents.slice();
      let updateEvents = false;
      if (action.params.method === 'POST') {
        const existArr = newEvents.filter((e) => e.id === action.response.id);
        const lastIndex = newEvents.length - 1;
        if (existArr.length > 0) {
          return { ...state, isFetching: false };
        }
        if (newEvents.length > 0) {
          newEvents[lastIndex] = action.response;
          for (let i = 0; i < newEvents.length; i += 1) {
            newEvents[i].local_id = i + 1;
          }
        }
        filteredEvents = newEvents;
      } else if (action.params.method === 'DELETE') {
        if (action.params.data.recurrences) {
          newEvents = newEvents.filter((e) => !(e.id === action.params.data.id && e.start_time === action.params.data.start_time));
        } else {
          newEvents = newEvents.filter((e) => e.id !== action.params.data.id);
        }
        filteredEvents = newEvents;
      } else if (action.params.method === 'PUT') {
        if (!action.params.data.recurrences || action.params.data.update_all) {
          filteredEvents = newEvents.filter((e) => e.id !== action.params.data.id);
        }

        const alreadyHaveEventArr = filteredEvents.filter((e) => e.id === action.response.id);
        if (alreadyHaveEventArr.length === 0 && !action.response.recurrences) {
          filteredEvents.push(action.response);
        } else if (action.response.recurrences) {
          // this is a repeating entry. Let's add all the repeats.
          const ruleset = recurrenceRuleSet({ string: action.response.recurrences, type: RRPT.mm });
          ruleset.timeset = [];

          // workaround to avoid today always being picked
          ruleset.options.dtstart = moment(state.selectedDate).toDate();

          // workaround to prevent us going to the end of time
          ruleset.options.until = moment(state.selectedDate).add(8, 'days').toDate();

          // eslint-disable-next-line no-underscore-dangle
          for (const rrule of ruleset._rrule) {
            rrule.timeset = [];
            if (rrule.options.freq === 2) {
              rrule.options.byweekday = [moment(action.response.start_time).weekday() - 1];
            }
          }

          const extdate = ruleset.valueOf().slice().pop();

          const excludedDates = extdate.includes('EXDATE:')
            ? extdate
                .slice(7)
                .split(',')
                .map((e) => moment(e).toISOString())
            : [];

          const betweenDates = ruleset.between(moment(state.selectedDate).subtract(7, 'days').toDate(), moment(state.selectedDate).add(7, 'days').toDate());

          for (const date of betweenDates) {
            const startTime = moment(action.response.start_time);
            const endTime = moment(action.response.end_time);
            const event = {
              ...action.response,

              start_time: moment(date)
                .set({
                  hour: startTime.get('hour'),
                  minute: startTime.get('minute'),
                  second: startTime.get('second'),
                  millisecond: 0,
                })
                .toISOString(),

              end_time: moment(date)
                .set({
                  hour: endTime.get('hour'),
                  minute: endTime.get('minute'),
                  second: endTime.get('second'),
                  millisecond: 0,
                })
                .toISOString(),
            };

            if (excludedDates.indexOf(event.start_time) === -1) filteredEvents.push(event);
          } // for date of betweenDates
        }
      }

      if (action.params.data.update_all) updateEvents = true;

      return { ...state, events: filteredEvents, update_events: updateEvents, isFetching: false };
    }

    case aTypeCreateUpdateTimeoff.fail:
      return { ...state, isFetching: false };

    case DELETE_APPOINTMENT_REQUEST: {
      let newProcedures = state.procedures.slice();
      newProcedures = state.procedures.filter((x) => x.appointment.id !== action.params.appointment);
      return { ...state, procedures: newProcedures };
    }

    case CHANGE_APPOINTMENT_STATUS_REQUEST: {
      const newProcedures = state.procedures.slice();
      const statusChange = newProcedures.filter((x) => x.appointment.id === action.params.appointments[0].id);
      for (let i = 0; i < statusChange.length; i += 1) {
        statusChange[i].appointment.status = action.params.appointments[0].status;
      }
      return { ...state, procedures: newProcedures, appointmentCancelled: false, isFetching: true };
    }

    case SET_VIEW: {
      const dayName = action.selectedDate.format('ddd').toLowerCase();
      const isSalonOpen = action.openingHours && action.openingHours.find((day) => day.shortName === dayName).open;
      return {
        ...state,
        dayView: action.dayView,
        selectedDate: action.selectedDate,
        stylistId: action.stylistId,
        isSalonOpen,
        defaultToWeekForOneStylist: false,
      };
    }

    case CHECKOUT_CLOSE_SESSION_SUCCESS:
      return { ...state, checkoutClosed: true };

    case SAVE_DAYDATE:
      return { ...state, currentDay: action.currentDay };

    case STYLISTS_SAVE_ORDER_REQUEST:
      return { ...state, stylistSaveOrder: { isFetching: true, response: null, error: null } };

    case STYLISTS_SAVE_ORDER_SUCCESS:
      return { ...state, stylistSaveOrder: { isFetching: false, response: action.response, error: null } };

    case HIDE_INACTIVE_STYLISTS:
      return { ...state, displayStylists: action.response, show_only_active: true };

    case SHOW_ALL_STYLISTS:
      return { ...state, displayStylists: action.stylistsList, show_only_active: false };

    case FETCH_APPOINTMENT_DETAILS_REQUEST: {
      const appointmentDetails = {};
      appointmentDetails[action.params.appointmentId] = { isFetching: true, success: false };
      return { ...state, appointmentDetails };
    }

    case FETCH_APPOINTMENT_DETAILS_SUCCESS: {
      const appointmentDetails = { ...state.appointmentDetails };

      appointmentDetails[action.response.id] = { isFetching: false, success: true, response: action.response };

      const bDayMonth = getAny(undefined, 'response.client.birthday', action);
      const appDate = getAny(undefined, 'response.procedures[0].start_time', action);

      let isClientBirthdayToday = false;
      if (bDayMonth) {
        if (!bDayMonth) {
          isClientBirthdayToday = false;
        } else if (moment(appDate).format('D') === String(bDayMonth[0]) && moment(appDate).format('M') === String(bDayMonth[1])) {
          isClientBirthdayToday = true;
        }
      }

      return { ...state, appointmentDetails, isClientBirthdayToday, preferenceList: [] };
    }

    case FETCH_APPOINTMENT_DETAILS_FAILURE: {
      const appointmentDetails = { ...state.appointmentDetails };
      appointmentDetails[action.params.appointmentId] = { isFetching: false, error: action.error };
      return { ...state, appointmentDetails };
    }

    case FETCH_CLIENT_LABELS_SUCCESS:
      return { ...state, clientLabels: action.response };

    case aTypeGetProcedureLog.pass:
      return { ...state, procedureLog: { isFetching: true, response: action.response, error: null } };

    case SCHEDULER_FETCH_DEPOSIT_FAILURE: {
      const { deposits } = state;
      deposits[action.params.appointmentId] = {
        isFetching: false,
        errorMessage: action.error.errorMessage || null,
        response: null,
      };
      return { ...state, deposits: { ...deposits } };
    }

    case SCHEDULER_ALLOW_CREATION: {
      const allowCreationCount = (state.allowCreationCount || 0) + (action.allowed ? -1 : 1);
      return { ...state, allowCreation: allowCreationCount < 1, allowCreationCount };
    }

    case SCHEDULER_ALLOW_MODAL: {
      const allowModalCount = (state.allowModalCount || 0) + (action.allowed ? -1 : 1);
      return { ...state, allowModal: allowModalCount < 1, allowModalCount };
    }

    case SCHEDULER_FETCH_DEPOSIT_REQUEST: {
      const { deposits } = state;
      deposits[action.params.appointmentId] = { isFetching: true, error: null, response: null };
      return { ...state, deposits: { ...deposits } };
    }

    case SCHEDULER_FETCH_DEPOSIT_SUCCESS: {
      const { deposits } = state;
      deposits[action.response.created_for_appointmentId] = {
        isFetching: false,
        error: null,
        response: action.response,
      };
      return { ...state, deposits: { ...deposits } };
    }

    case GET_PREFERENCE_SUCCESS: {
      const preferenceList = [...state.preferenceList];
      action.response.map((item) => {
        if (preferenceList.filter((c) => c.id === item.id).length === 0) preferenceList.push(item);
        return true;
      });
      return { ...state, preferenceList };
    }

    case SAVE_PREFERENCE_SUCCESS: {
      const preferenceList = [...state.preferenceList];
      if (preferenceList.filter((c) => c.id === action.response.id).length === 0) preferenceList.push(action.response);
      return { ...state, preferenceList };
    }

    case DELETE_PREFERENCE_SUCCESS: {
      const currPreferenceList = [...state.preferenceList];
      const preferenceList = currPreferenceList.filter((c) => c.id !== action.params.preferenceId);
      return { ...state, preferenceList };
    }

    case aTypeMarkAppointmentEdit.name: {
      const editing = !!action?.mark;

      const procedures = state.procedures.map((p) => (p?.appointment?.id === action.appointmentId ? { ...p, editing } : p));

      return { ...state, procedures };
    }

    case SET_BOOKED_BY_BUTTON:
      return { ...state, bookedByButton: action.tf };

    case aTypeUndoProcedure.init:
      return { ...state };

    case aTypeUndoProcedure.pass:
      return { ...state, procedureDropTimeUndone: true, procedureDropTime: false };

    case aTypeUndoProcedure.fail:
      return { ...state };

    case SET_DROP_TIME:
      return { ...state, procedureDropTime: action.dropTime, procedureDropTimeUndone: false };

    case SET_DRAG_FROM:
      return { ...state, procedureDragFrom: action.stylistId };

    case CHARGE_NOSHOW_REQUEST:
      return { ...state, isChargingNoshow: true, noshowChargeSuccess: false };

    case CHARGE_NOSHOW_SUCCESS: {
      const noshowChargeSuccess = !action.response.error;
      const noshowChargeError = action.response.error !== '' && action.response.error !== undefined;
      return {
        ...state,
        isChargingNoshow: false,
        noshowChargeSuccess,
        noshowChargeError,
        noshow_appointment_id: action.params.appointment_id,
        noshowChargedAmount: action.response.paid / 100,
        errorMessage: action.response.error,
      };
    }

    case CLEAR_NOSHOW_CHARGE_INFO: {
      return {
        ...state,
        noshowChargeSuccess: false,
        noshowChargeError: false,
        noshow_appointment_id: 0,
        depositData: {},
        noshowChargedAmount: 0,
        noshow_action: '',
        errorMessage: '',
        appointmentCancelled: false,
      };
    }

    case CHARGE_NOSHOW_FAILURE:
      return { ...state, isChargingNoshow: false, noshowChargeError: true, noshowChargeSuccess: false };

    case REQUEST_DEPOSIT_REQUEST:
      return { ...state, isRequestingDeposit: true, requestDepositSuccess: false };

    case REQUEST_DEPOSIT_SUCCESS:
      return { ...state, isRequestingDeposit: false, requestDepositSuccess: true };

    case CLEAR_REQUEST_DEPOSIT_SUCCESS:
      return { ...state, requestDepositSuccess: false };

    case REQUEST_DEPOSIT_FAILURE:
      return { ...state, isRequestingDeposit: false, requestDepositSuccess: false };

    case UPDATE_APPOINTMENT_WITH_SERVICES_REQUEST: {
      const { id } = action.params.data.procedures[0];
      const procedures = action.params.data.procedures.slice();
      const { start_time: startTime } = procedures[0];
      return { ...state, procedureChanged: { id, start_time: startTime } };
    }

    case SMS_CREDITS_PUSHER: {
      const { sms_credits_available: smsCreditsAvailable } = innerData;
      return { ...state, sms_credits_available: smsCreditsAvailable };
    }

    default:
      return state;
  }
};

export default scheduleReducer;
